import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Rating from '../components/product/Rating';
import Message from '../components/Message';
import Loader from '../components/Loader';
import {
  listProductDetails,
  createProductReview,
} from '../actions/productActions';
import { PRODUCT_REVIEW_CREATE_RESET } from '../constants/productConstants';

const ProductScreen = () => {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [activeTab, setActiveTab] = useState('description');
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  const dispatch = useDispatch();
  const { id } = useParams();

  const productDetails = useSelector((state) => state.productDetails);
  const { loading, error, product } = productDetails;

  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  const productReviewCreate = useSelector((state) => state.productReviewCreate);
  const {
    success: successProductReview,
    loading: loadingProductReview,
    error: errorProductReview,
  } = productReviewCreate;

  useEffect(() => {
    if (successProductReview) {
      setRating(0);
      setComment('');
      dispatch({ type: PRODUCT_REVIEW_CREATE_RESET });
    }
    dispatch(listProductDetails(id));
  }, [dispatch, id, successProductReview]);


  const submitHandler = (e) => {
    e.preventDefault();
    dispatch(
      createProductReview(id, {
        rating,
        comment,
      })
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="mb-8">
          <Link 
            className="inline-flex items-center text-gray-600 hover:text-primary transition-colors duration-200"
            to="/"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Products
          </Link>
        </nav>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader />
          </div>
        ) : error ? (
          <Message variant="danger">{error}</Message>
        ) : (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12">
              {/* Enhanced Product Images */}
              <div className="space-y-4">
                <div className="relative bg-white rounded-2xl shadow-lg overflow-hidden group">
                  <div className="aspect-square">
                    <img
                      src={(product.images && product.images[activeImageIndex]) || product.image || 'https://via.placeholder.com/600x600/F3F4F6/9CA3AF?text=No+Image'}
                      alt={product.name}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/600x600/F3F4F6/9CA3AF?text=No+Image';
                      }}
                    />
                  </div>
                  {product.countInStock === 0 && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <span className="bg-red-500 text-white px-4 py-2 rounded-lg font-semibold">Out of Stock</span>
                    </div>
                  )}
                </div>
                
                {product.images && product.images.length > 1 && (
                  <div className="flex space-x-3 overflow-x-auto pb-2">
                    {product.images.map((image, index) => (
                      <button
                        key={index}
                        className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all duration-200 ${
                          activeImageIndex === index
                            ? 'border-primary shadow-lg scale-105'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => setActiveImageIndex(index)}
                      >
                        <img
                          src={image}
                          alt={`${product.name} thumbnail ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Enhanced Product Info */}
              <div className="space-y-6">
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    {product.priority && (
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        product.priority === 'high' ? 'bg-red-100 text-red-800' :
                        product.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {product.priority.toUpperCase()} PRIORITY
                      </span>
                    )}
                    {product.category && (
                      <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
                        {product.category}
                      </span>
                    )}
                  </div>
                  
                  <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">{product.name}</h1>
                  
                  <div className="flex items-center gap-4 mb-6">
                    <Rating
                      value={product.rating}
                      text={`${product.numReviews} reviews`}
                    />
                    <div className="flex items-center text-sm text-gray-500">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      {product.views || 0} views
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-2xl p-6 shadow-lg">
                  <div className="mb-6">
                    <div className="flex items-center justify-center mb-6 p-4 bg-green-50 rounded-xl border border-green-200">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center">
                          <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.097"/>
                          </svg>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-green-700">WhatsApp for Pricing</p>
                          <p className="text-xs text-green-600">Best rates & bulk discounts available</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between py-4 border-y border-gray-200">
                    <span className="font-medium text-gray-900">Availability:</span>
                    <div className="flex items-center">
                      <div className={`w-2 h-2 rounded-full mr-2 ${product.countInStock > 0 ? 'bg-green-500' : 'bg-red-500'}`}></div>
                      <span className={`font-medium ${product.countInStock > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {((product.countInStock > 0 && product.countInStock <= 10) || product.countInStock === 0) && (
                          <span>
                            {product.countInStock} in stock
                          </span>
                        )}
                      </span>
                    </div>
                  </div>


                  <div className="space-y-3 pt-4">
                    <a
                      href={`https://wa.me/254710140890?text=Hello%20iKappalSafety!%0A%0AI'm%20interested%20in%20the%20following%20product:%0A%0A📦%20Product:%20${encodeURIComponent(product.name)}%0A🏷️%20Brand:%20${encodeURIComponent(product.brand)}%0A📂%20Category:%20${encodeURIComponent(product.category)}%0A⭐%20Rating:%20${product.rating}/5%20(${product.numReviews}%20reviews)%0A📋%20Description:%20${encodeURIComponent(product.description || 'Professional safety equipment')}%0A%0APlease%20provide:%0A✅%20Current%20pricing%0A✅%20Availability%20status%0A✅%20Delivery%20options%0A✅%20Bulk%20pricing%20(if%20applicable)%0A%0AThank%20you!`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full py-4 bg-green-600 hover:bg-green-700 text-white rounded-xl font-semibold text-lg transition-all duration-200 flex items-center justify-center space-x-2 transform hover:scale-[1.02] shadow-lg hover:shadow-xl"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.097"/>
                      </svg>
                      <span>Order via WhatsApp</span>
                    </a>

                    <a
                      href={`https://wa.me/254710140890?text=Hello%20iKappalSafety!%0A%0AI%20need%20a%20detailed%20quote%20for:%0A%0A📦%20Product:%20${encodeURIComponent(product.name)}%0A🏷️%20Brand:%20${encodeURIComponent(product.brand)}%0A📂%20Category:%20${encodeURIComponent(product.category)}%0A%0APlease%20provide%20a%20comprehensive%20quote%20including:%0A💰%20Unit%20pricing%0A📦%20Bulk%20discounts%0A🚚%20Delivery%20costs%0A📋%20Technical%20specifications%0A🛡️%20Warranty%20information%0A📄%20Compliance%20certificates%0A%0AThank%20you!`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full py-4 border-2 border-green-600 text-green-600 hover:bg-green-600 hover:text-white rounded-xl font-semibold text-lg transition-all duration-200 flex items-center justify-center space-x-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span>Request Detailed Quote</span>
                    </a>
                  </div>
                </div>

                {/* Quick Info Cards */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white rounded-xl p-4 shadow-md text-center">
                    <svg className="w-8 h-8 text-green-500 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <p className="text-sm font-medium text-gray-900">Quality Assured</p>
                    <p className="text-xs text-gray-500">ISO Certified</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-md text-center">
                    <svg className="w-8 h-8 text-blue-500 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                    </svg>
                    <p className="text-sm font-medium text-gray-900">Fast Shipping</p>
                    <p className="text-xs text-gray-500">2-3 Days</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Enhanced Product Details Tabs */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="border-b border-gray-200">
                <nav className="flex overflow-x-auto">
                  <button
                    onClick={() => setActiveTab('description')}
                    className={`flex-shrink-0 py-4 px-6 font-semibold border-b-2 transition-colors duration-200 ${
                      activeTab === 'description'
                        ? 'border-primary text-primary bg-primary/5'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <svg className="w-5 h-5 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Description
                  </button>
                  <button
                    onClick={() => setActiveTab('specifications')}
                    className={`flex-shrink-0 py-4 px-6 font-semibold border-b-2 transition-colors duration-200 ${
                      activeTab === 'specifications'
                        ? 'border-primary text-primary bg-primary/5'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <svg className="w-5 h-5 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    Specifications
                  </button>
                  <button
                    onClick={() => setActiveTab('reviews')}
                    className={`flex-shrink-0 py-4 px-6 font-semibold border-b-2 transition-colors duration-200 ${
                      activeTab === 'reviews'
                        ? 'border-primary text-primary bg-primary/5'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <svg className="w-5 h-5 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                    </svg>
                    Reviews ({product.numReviews})
                  </button>
                </nav>
              </div>

              <div className="p-8">
                {activeTab === 'description' && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-4">Product Description</h3>
                      <p className="text-gray-700 leading-relaxed text-lg">{product.description}</p>
                    </div>

                    {product.features && product.features.length > 0 && (
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-4">Key Features</h3>
                        <div className="grid gap-3">
                          {product.features.map((feature, index) => (
                            <div key={index} className="flex items-start space-x-3">
                              <svg className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              <span className="text-gray-700">{feature}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {product.certifications && product.certifications.length > 0 && (
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-4">Certifications & Standards</h3>
                        <div className="flex flex-wrap gap-3">
                          {product.certifications.map((cert, index) => (
                            <span
                              key={index}
                              className="bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800 px-4 py-2 rounded-full text-sm font-medium border border-blue-200"
                            >
                              {cert}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'specifications' && (
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-6">Technical Specifications</h3>
                    {product.specifications && Object.keys(product.specifications).length > 0 ? (
                      <div className="overflow-hidden border border-gray-200 rounded-xl">
                        <table className="min-w-full divide-y divide-gray-200">
                          <tbody className="bg-white divide-y divide-gray-200">
                            {Object.entries(product.specifications).map(([key, value], index) => (
                              <tr key={key} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                                <td className="px-6 py-4 text-sm font-semibold text-gray-900 w-1/3">
                                  {key}
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-700">{value}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <p className="text-gray-500 text-lg">No specifications available for this product.</p>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'reviews' && (
                  <div className="space-y-8">
                    <h3 className="text-xl font-bold text-gray-900">Customer Reviews</h3>
                    
                    {product.reviews && product.reviews.length === 0 && (
                      <div className="text-center py-12">
                        <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                        <p className="text-gray-500 text-lg mb-4">No reviews yet</p>
                        <p className="text-gray-400">Be the first to review this product</p>
                      </div>
                    )}
                    
                    {product.reviews && product.reviews.length > 0 && (
                      <div className="space-y-6">
                        {product.reviews.map((review) => (
                          <div key={review._id} className="bg-gray-50 rounded-xl p-6">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                                  <span className="text-white font-semibold text-sm">
                                    {review.name.charAt(0).toUpperCase()}
                                  </span>
                                </div>
                                <div>
                                  <p className="font-semibold text-gray-900">{review.name}</p>
                                  <p className="text-sm text-gray-500">
                                    {new Date(review.createdAt).toLocaleDateString('en-US', {
                                      year: 'numeric',
                                      month: 'long',
                                      day: 'numeric'
                                    })}
                                  </p>
                                </div>
                              </div>
                              <Rating value={review.rating} />
                            </div>
                            <p className="text-gray-700 leading-relaxed">{review.comment}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="border-t border-gray-200 pt-8">
                      <h4 className="text-lg font-semibold text-gray-900 mb-6">Write a Review</h4>
                      
                      {errorProductReview && (
                        <Message variant="danger">{errorProductReview}</Message>
                      )}
                      
                      {userInfo ? (
                        <form onSubmit={submitHandler} className="space-y-6">
                          <div>
                            <label htmlFor="rating" className="block text-sm font-semibold text-gray-900 mb-2">
                              Rating *
                            </label>
                            <select
                              id="rating"
                              required
                              value={rating}
                              onChange={(e) => setRating(e.target.value)}
                              className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:ring-2 focus:ring-primary focus:border-primary"
                            >
                              <option value="">Select a rating...</option>
                              <option value="1">1 - Poor</option>
                              <option value="2">2 - Fair</option>
                              <option value="3">3 - Good</option>
                              <option value="4">4 - Very Good</option>
                              <option value="5">5 - Excellent</option>
                            </select>
                          </div>
                          
                          <div>
                            <label htmlFor="comment" className="block text-sm font-semibold text-gray-900 mb-2">
                              Your Review *
                            </label>
                            <textarea
                              id="comment"
                              rows="4"
                              required
                              value={comment}
                              onChange={(e) => setComment(e.target.value)}
                              placeholder="Share your experience with this product..."
                              className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:ring-2 focus:ring-primary focus:border-primary"
                            ></textarea>
                          </div>
                          
                          <button
                            disabled={loadingProductReview}
                            type="submit"
                            className="bg-primary text-white py-3 px-8 rounded-lg hover:bg-primary-dark font-semibold transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {loadingProductReview ? 'Submitting...' : 'Submit Review'}
                          </button>
                        </form>
                      ) : (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
                          <svg className="w-12 h-12 text-blue-500 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          <p className="text-gray-700 mb-4">Please sign in to write a review</p>
                          <Link 
                            to="/login" 
                            className="inline-flex items-center px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark font-semibold transition-colors duration-200"
                          >
                            Sign In
                          </Link>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ProductScreen;
